<script setup lang="ts">
import { type PropType, ref } from 'vue'
import dayjs, { Dayjs } from 'dayjs'
import { $t } from '@previo/core'
import type { PValidationRule } from '../../../types/types'
import type { PDatePickerFastChoice } from '../../date-picker/types'
import { PTimeInputFieldIncrement } from '../types'
import { usePrevioDS } from '../../../composables/helpers'
import PBtn from '../../btn/PBtn.vue'
import { PBtnType } from '../../btn/types'
import PCard from '../../card/PCard.vue'
import PSpace from '../../space/PSpace.vue'
import { PSpaceSize } from '../../space/types'
import PDateTimePicker from '../../date-time-picker/PDateTimePicker.vue'
import DateTimeInputField from './components/date-time-input-field/DateTimeInputField.vue'
import DateTimeContextMenu from './components/date-time-input-field/DateTimeContextMenu.vue'

type Emits = {
  (e: 'confirm'): void
}

const emits = defineEmits<Emits>()

const props = defineProps({
  label: {
    type: String,
    default: undefined,
  },
  tooltip: {
    type: String,
    default: null,
  },
  hint: {
    type: String,
    default: undefined,
  },
  timeIncrement: {
    type: Number,
    default: PTimeInputFieldIncrement.QUARTER,
  },
  rules: {
    type: Array as PropType<PValidationRule[]>,
    default: () => [],
  },
  fastChoices: {
    type: Array as PropType<PDatePickerFastChoice[]>,
    default: () => [],
  },
  min: {
    type: Object as PropType<Dayjs>,
    default: undefined,
  },
  max: {
    type: Object as PropType<Dayjs>,
    default: undefined,
  },
  timeStrict: {
    type: Boolean,
    default: false,
  },
  disable: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  disableClearable: {
    type: Boolean,
    default: false,
  },
  hideTimeField: {
    type: Boolean,
    default: false,
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
  includeTimeValidation: {
    type: Boolean,
    default: false,
  },
  cy: {
    type: String,
    required: true,
  },
  defaultTime: {
    type: String,
    validator: (value?: string): boolean => {
      if (!value) return true
      return dayjs(value, 'HH:mm', true).isValid()
    },
    required: false,
  },
  format: {
    type: String,
    default: undefined,
  },
  hideConfirmButton: {
    type: Boolean,
    default: false,
  },
  confirmOnFastChoice: {
    type: Boolean,
    default: false,
  },
})

const model = defineModel<Dayjs | null>({ default: null })

const ds = usePrevioDS()

const isDateTimePickerVisible = ref<boolean>(false)

const onOpenPicker = (): void => {
  if (props.readonly || props.disable) {
    return
  }

  isDateTimePickerVisible.value = true
}

const onClosePicker = (): void => {
  isDateTimePickerVisible.value = false
}

const onConfirmClick = (): void => {
  isDateTimePickerVisible.value = false
  emits('confirm')
}

const onFastChoiceSelect = (): void => {
  if (props.confirmOnFastChoice) {
    onConfirmClick()
    return
  }

  if (props.hideConfirmButton) {
    onClosePicker()
  }
}

const onDaySelect = (): void => {
  if (props.hideConfirmButton) {
    onClosePicker()
  }
}

defineExpose({ openPicker: onOpenPicker })
</script>

<template>
  <date-time-input-field
    v-model="model"
    :cy="cy"
    :hide-time-field="hideTimeField"
    :time-increment="timeIncrement"
    :disable="disable"
    :readonly="readonly"
    :rules="rules"
    :min="min"
    :max="max"
    :disable-clearable="disableClearable"
    :include-time-validation="includeTimeValidation"
    :autofocus="autofocus"
    :label="label"
    :tooltip="tooltip"
    :hint="hint"
    :default-time="defaultTime"
    :format="format"
    @open-picker="onOpenPicker"
    @clear="model = null"
  >
    <template #context-menu>
      <date-time-context-menu
        v-model="isDateTimePickerVisible"
        :title="$t('Select date')"
        cy="date-time-picker-context-menu"
      >
        <p-date-time-picker
          v-model="model"
          :min="min"
          :max="max"
          :time-increment="timeIncrement"
          :hide-time-field="hideTimeField"
          :disable-clearable="disableClearable"
          :fast-choices="fastChoices"
          :strict="timeStrict"
          :disable="disable"
          :default-time="defaultTime"
          @select="onFastChoiceSelect"
          @day-select="onDaySelect"
        />

        <p-space :mobile="PSpaceSize.SM" :laptop="PSpaceSize.SM" vertical />

        <p-card v-if="!hideConfirmButton" class="p-flex p-items-center p-no-wrap">
          <p-space :mobile="PSpaceSize.ZERO" :laptop="PSpaceSize.GROW" />

          <template v-if="ds.screen().isMobile">
            <p-btn
              :text="$t('Close')"
              :type="PBtnType.TERTIARY"
              grow
              cy="ds-date-time-picker-close-btn"
              @click="onClosePicker"
            />
            <p-space :mobile="PSpaceSize.SM" />
          </template>

          <p-btn
            :icon-end="ds.screen().isMobile ? null : 'check'"
            :text="$t('Confirm')"
            :grow="ds.screen().isMobile"
            cy="ds-date-time-picker-confirm-btn"
            @click="onConfirmClick"
          />
        </p-card>
      </date-time-context-menu>
    </template>
  </date-time-input-field>
</template>
